import api from "./api-service";
import axios from "axios";
import { getBrowserInfo } from "@/utils/browser-info";

/**
 * Inicia novo draft e recebe presigned URL
 */
export const initDraft = async () => {
  const response = await api().post("/therapy-draft/init");
  return response.data;
};

/**
 * Lista drafts do terapeuta
 */
export const listDrafts = async () => {
  const response = await api().get("/therapy-draft");
  return response.data;
};

/**
 * Busca metadados do draft
 */
export const getDraft = async (draftId) => {
  const response = await api().get(`/therapy-draft/${draftId}`);
  return response.data;
};

/**
 * Busca conteúdo completo do draft do S3
 */
export const getDraftContent = async (draftId) => {
  try {
    const response = await api().get(`/therapy-draft/${draftId}/content`, {
      timeout: 30000, // 30 segundos
    });
    return response.data;
  } catch (error) {
    console.error("[getDraftContent] Erro ao buscar conteúdo do S3:", error);

    // Registra erro no backend
    try {
      const browserInfo = getBrowserInfo();
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.code === "ERR_NETWORK";

      await logBackupError(draftId, {
        errorType: isNetworkError ? "NetworkError" : "S3GetError",
        errorMessage:
          error.message || "Erro desconhecido ao recuperar draft do S3",
        httpStatus: error.response?.status || null,
        isNetworkError,
        browser: browserInfo.browser,
        os: browserInfo.os,
        userAgent: navigator.userAgent,
        additionalInfo: {
          step: "getDraftContent",
          errorCode: error.code,
          errorResponse: error.response?.data,
        },
      });
    } catch (logError) {
      console.error("[getDraftContent] Erro ao registrar erro:", logError);
    }

    // Repropaga o erro
    throw error;
  }
};

/**
 * Atualiza metadados do draft (ex: current_step)
 */
export const updateDraft = async (draftId, data) => {
  const response = await api().patch(`/therapy-draft/${draftId}`, data);
  return response.data;
};

/**
 * Renova presigned URL se expirar
 */
export const renewPresignedUrl = async (draftId) => {
  const response = await api().post(
    `/therapy-draft/${draftId}/renew-presigned-url`
  );
  return response.data;
};

/**
 * Deleta draft (banco + S3)
 */
export const deleteDraft = async (draftId) => {
  const response = await api().delete(`/therapy-draft/${draftId}`);
  return response.data;
};

/**
 * Faz backup direto no S3 via presigned URL
 */
export const backupToS3 = async (presignedUrl, therapyData) => {
  try {
    /**
     * 🔥 FIX: File/Blob objects não podem ser serializados via JSON.stringify (viram {}).
     * Se o upload eager ainda não completou, o arquivo é excluído do backup desta rodada.
     * O próximo ciclo de backup (30s) irá capturá-lo já como metadado com URL.
     */
    const serializeAttachment = (file) => {
      if (!file) return null;
      if (
        (typeof File !== "undefined" && file instanceof File) ||
        (typeof Blob !== "undefined" && file instanceof Blob)
      ) {
        return null; // File não serializado — pendente de upload eager
      }
      return file; // Já é metadado com resourceUrl
    };

    // Remove campos pesados (imagens) para reduzir payload
    const lightPayload = {
      ...therapyData,
      // 🔥 Filtra File objects de additionalFiles e signedConsentTerm
      additionalFiles: (therapyData.additionalFiles || [])
        .map(serializeAttachment)
        .filter(Boolean),
      signedConsentTerm: serializeAttachment(therapyData.signedConsentTerm),
      diagnose: therapyData.diagnose
        ? {
            ...therapyData.diagnose,
            tumoralPhenomena: therapyData.diagnose.tumoralPhenomena?.map(
              (tp) => ({
                ...tp,
                images: [], // Remove imagens
              })
            ),
            vascularPhenomena: therapyData.diagnose.vascularPhenomena?.map(
              (vp) => ({
                ...vp,
                images: [],
              })
            ),
            potentialSpaces: therapyData.diagnose.potentialSpaces?.map(
              (ps) => ({
                ...ps,
                images: [],
              })
            ),
            flowTypes: therapyData.diagnose.flowTypes?.map((ft) => ({
              ...ft,
              images: [],
            })),
            chakras: therapyData.diagnose.chakras?.map((c) => ({
              ...c,
              images: [],
            })),
          }
        : null,
    };

    const payloadString = JSON.stringify(lightPayload);
    const payloadSize = new Blob([payloadString]).size;

    // PUT direto no S3
    const response = await axios.put(presignedUrl, payloadString, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 45000, // 45 segundos
    });

    return response;
  } catch (error) {
    // Captura informações detalhadas do erro
    const errorInfo = {
      status: error.response?.status,
      message: error.message,
      isNetworkError: !error.response,
      code: error.code,
      url: presignedUrl?.substring(0, 100) + "...", // Trunca URL para log
    };

    console.error("[S3 Backup] Erro no PUT:", errorInfo);

    // Lança erro para ser tratado pelo caller
    throw error;
  }
};

/**
 * Envia log de erro para o backend
 */
export const logBackupError = async (errorData) => {
  try {
    const { browser, os, userAgent } = getBrowserInfo();

    const payload = {
      ...errorData,
      browser,
      os,
      userAgent,
    };

    await api().post("/therapy-draft/error", payload);
  } catch (error) {
    // Falha silenciosa - não queremos interromper o app por falha no log
    console.error("[Error Log] Falha ao enviar log de erro:", error);
  }
};
